import React, { forwardRef, useEffect, useState, useRef } from "react";
import api, { API } from "../lib/api";

/**
 * AuthVideo – henter signert korttidsholdbar URL (60s TTL) fra
 * /api/media/sign for videoer. Signaturen dør raskt, så URL-er
 * kopiert fra DevTools kan ikke deles videre utenfor plattformen.
 *
 * Bruker forwardRef så parent kan styre <video>-elementet
 * (play/pause, IntersectionObserver auto-play, etc.).
 */
const AuthVideo = forwardRef(function AuthVideo(
  { videoId, poster, fallback = null, className = "", ...videoProps },
  ref
) {
  const [src, setSrc] = useState(null);
  const [locked, setLocked] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (!videoId) { setSrc(null); return; }
    let cancelled = false;

    const refresh = async () => {
      try {
        const r = await api.get(`/media/sign`, { params: { kind: "video", id: videoId } });
        if (r.status !== 200) {
          if (!cancelled) setSrc(null);
          return;
        }
        const j = r.data;
        if (cancelled) return;
        setSrc(`${API.replace(/\/api$/, "")}${j.url}`);
        clearTimeout(timer.current);
        timer.current = setTimeout(refresh, 55_000);
      } catch (e) {
        if (!cancelled) {
          setSrc(null);
          setLocked(e?.response?.data?.detail?.code === "locked_vip_video");
        }
      }
    };
    refresh();
    return () => {
      cancelled = true;
      clearTimeout(timer.current);
    };
  }, [videoId]);

  if (!videoId) return fallback || <div className={`bg-zinc-900 ${className}`} />;
  if (!src && locked) return (<div className={`${className} bg-zinc-900 relative flex flex-col items-center justify-center gap-1`} data-testid="media-locked-vip-video">
        <span className="px-2 py-0.5 bg-[var(--gold)] text-[var(--noir)] text-[10px] font-bold uppercase tracking-wider">VIP</span>
        <span className="font-meta text-[10px] uppercase tracking-[0.15em] text-[var(--cream)]">Film krever VIP</span>
      </div>);
  if (!src) return <div className={`${className} bg-zinc-900`} />;

  return (
    <video
      ref={ref}
      key={src}
      src={src}
      poster={poster || undefined}
      className={className}
      {...videoProps}
    />
  );
});

export default AuthVideo;
